import { useCallback, useMemo, useRef, useState } from 'react';
import Box from '@mui/material/Box';
import { alpha, type Theme } from '@mui/material/styles';
import { useSnackbar } from 'notistack';

import Avatar from '@/components/Avatar/OutlineAvatar';
import { IconCamera as PhotoCameraIcon } from '@/components/icon/tabler/IconCamera';
import { IconX as CloseIcon } from '@/components/icon/tabler/IconX';
import { IconPhoto as ImageIcon } from '@/components/icon/tabler/IconPhoto';
import { CristaSpinner } from '@/components/Loaders';
import { useFileUpload } from '@/api/hooks';
import { gray } from '@/theme/themePrimitives';
import ImageCropModal from '@/components/AvatarUploader/ImageCropModal';
import { type ModalRef } from '@/components/Modal';
import { validateImageCropFile } from '@/utils/image';
import { useTranslate } from '@/state/i18n';

export interface AvatarUploaderProps {
  imageUrl?: string | null;
  size?: number;
  fontSize?: number;
  alt?: string;
  onImageChange?: (fileUrl: string) => void;
  onFileSelect?: (file: File) => void | Promise<void>;
  cropModalTitle?: string;
  cropShape?: 'rect' | 'round';
  cropAspect?: number;
  cropMaxOutputSize?: number;
  cropQuality?: number;
  cropOutputMimeType?: string;
  acceptedMimeTypes?: string[];
  maxFileSizeBytes?: number;
  invalidFileMessage?: string;
  disabled?: boolean;
  accept?: string;
  onRemove?: () => void;
  /** S3 key prefix for the upload (the portal uses `partners`). */
  uploadFolder?: string;
  style?: Partial<{
    avatar: React.CSSProperties;
  }>;
}

const AvatarUploader = ({
  imageUrl,
  size = 72,
  fontSize = 32,
  alt: altProp,
  onImageChange,
  onFileSelect,
  cropModalTitle,
  cropShape = 'round',
  cropAspect = 1,
  cropMaxOutputSize = 1024,
  cropQuality = 0.84,
  cropOutputMimeType,
  acceptedMimeTypes = ['image/jpeg', 'image/png', 'image/webp'],
  maxFileSizeBytes = 5 * 1024 * 1024,
  invalidFileMessage: invalidFileMessageProp,
  disabled = false,
  accept = 'image/*',
  onRemove,
  uploadFolder,
  style,
}: AvatarUploaderProps) => {
  const t = useTranslate('components.AvatarUploader.index');
  const alt = altProp ?? t('altDefault');
  const invalidFileMessage = invalidFileMessageProp ?? t('invalidFile');
  const fileInputRef = useRef<HTMLInputElement>(null);
  const cropModalRef = useRef<ModalRef>(null);
  const [localImageUrl, setLocalImageUrl] = useState<string | null>(null);
  const [cropFile, setCropFile] = useState<File | null>(null);
  const [isUploading, setIsUploading] = useState(false);
  const { uploadFileWithPresignedUrl, isLoading: isPresignedLoading } = useFileUpload({
    folder: uploadFolder,
  });
  const { enqueueSnackbar } = useSnackbar();

  const cropEnabled = Boolean(cropModalTitle);

  const cropProps = useMemo(
    () =>
      cropEnabled && cropModalTitle
        ? {
            cropModalTitle,
            cropShape,
            cropAspect,
            cropMaxOutputSize,
            cropQuality,
            cropOutputMimeType,
            acceptedMimeTypes,
            maxFileSizeBytes,
            invalidFileMessage,
          }
        : null,
    [
      cropEnabled,
      cropModalTitle,
      cropShape,
      cropAspect,
      cropMaxOutputSize,
      cropQuality,
      cropOutputMimeType,
      acceptedMimeTypes,
      maxFileSizeBytes,
      invalidFileMessage,
    ],
  );

  const uploadCroppedFile = useCallback(
    async (file: File) => {
      if (onFileSelect) {
        await onFileSelect(file);
        return;
      }

      const uploadedUrl = await uploadFileWithPresignedUrl(file);
      if (uploadedUrl) {
        setLocalImageUrl(uploadedUrl);
        onImageChange?.(uploadedUrl);
      }
    },
    [onFileSelect, onImageChange, uploadFileWithPresignedUrl],
  );

  const handleFileChange = useCallback(
    async (event: React.ChangeEvent<HTMLInputElement>) => {
      const file = event.target.files?.[0];
      event.target.value = '';
      if (!file) return;

      if (cropProps) {
        const validationError = validateImageCropFile(file, cropProps);
        if (validationError) {
          enqueueSnackbar(validationError, { variant: 'error' });
          return;
        }
        setCropFile(file);
        cropModalRef.current?.open();
        return;
      }

      try {
        await uploadCroppedFile(file);
      } catch (error) {
        console.error('Error uploading image:', error);
        setLocalImageUrl(null);
      }
    },
    [cropProps, enqueueSnackbar, uploadCroppedFile],
  );

  const handleCropConfirm = useCallback(
    async (file: File) => {
      setIsUploading(true);
      try {
        await uploadCroppedFile(file);
      } catch (error) {
        console.error('Error uploading cropped image:', error);
      } finally {
        setIsUploading(false);
      }
    },
    [uploadCroppedFile],
  );

  const handleCropDismiss = useCallback(() => {
    setCropFile(null);
  }, []);

  const displayImage = useMemo(() => imageUrl || localImageUrl, [imageUrl, localImageUrl]);
  const isLoading = useMemo(
    () => disabled || isPresignedLoading || isUploading,
    [disabled, isPresignedLoading, isUploading],
  );

  const handleAvatarClick = useCallback(() => {
    if (!isLoading) {
      fileInputRef.current?.click();
    }
  }, [isLoading]);

  const handleRemove = useCallback(
    (e: React.MouseEvent) => {
      e.stopPropagation();
      setLocalImageUrl(null);
      onRemove?.();
    },
    [onRemove],
  );

  const camSize = Math.max(22, Math.round(size * 0.28));

  return (
    <>
      <Box sx={{ position: 'relative', width: size, height: size, flexShrink: 0 }}>
        <Avatar
          src={displayImage || undefined}
          alt={alt}
          sx={{
            width: size,
            height: size,
            fontSize: fontSize,
            cursor: isLoading ? 'default' : 'pointer',
            border: displayImage ? `1px solid ${gray[200]}` : `1.5px dashed ${gray[300]}`,
            backgroundColor: displayImage ? 'transparent' : gray[50],
            color: gray[400],
            transition: 'border-color .2s, background-color .2s',
            ...(!isLoading && !displayImage
              ? {
                  '&:hover': {
                    borderColor: gray[500],
                    backgroundColor: gray[100],
                  },
                }
              : {}),
            ...style?.avatar,
          }}
          onClick={handleAvatarClick}
        >
          {!displayImage && <ImageIcon sx={{ fontSize: fontSize * 0.6 }} />}
        </Avatar>

        {!isLoading && (
          <Box onClick={handleAvatarClick} sx={sx.cameraButton({ camSize })}>
            <PhotoCameraIcon />
          </Box>
        )}

        {!isLoading && displayImage && onRemove && (
          <Box onClick={handleRemove} sx={sx.removeButton({ camSize })}>
            <CloseIcon />
          </Box>
        )}

        {isLoading && (
          <Box sx={sx.loadingOverlay}>
            <CristaSpinner size={Math.round(fontSize * 0.6)} />
          </Box>
        )}
      </Box>

      <input
        ref={fileInputRef}
        type="file"
        accept={accept}
        style={{ display: 'none' }}
        onChange={handleFileChange}
        disabled={isLoading}
      />

      {cropProps ? (
        <ImageCropModal
          ref={cropModalRef}
          file={cropFile}
          onConfirm={handleCropConfirm}
          onDismiss={handleCropDismiss}
          cropModalTitle={cropProps.cropModalTitle}
          cropShape={cropProps.cropShape}
          cropAspect={cropProps.cropAspect}
          maxOutputSize={cropProps.cropMaxOutputSize}
          quality={cropProps.cropQuality}
          outputMimeType={cropProps.cropOutputMimeType}
        />
      ) : null}
    </>
  );
};

export default AvatarUploader;

const sx = {
  cameraButton: ({ camSize }: { camSize: number }) => ({
    position: 'absolute',
    bottom: 2,
    right: 2,
    width: camSize,
    height: camSize,
    borderRadius: '50%',
    backgroundColor: gray[900],
    color: 'common.white',
    border: '2px solid',
    borderColor: 'common.white',
    display: 'flex',
    alignItems: 'center',
    justifyContent: 'center',
    cursor: 'pointer',
    transition: 'background-color .15s',
    '&:hover': {
      backgroundColor: gray[700],
    },
    '& svg': { fontSize: camSize * 0.5 },
  }),
  removeButton: ({ camSize }: { camSize: number }) => ({
    position: 'absolute',
    top: 2,
    right: 2,
    width: camSize,
    height: camSize,
    borderRadius: '50%',
    backgroundColor: alpha(gray[400], 0.8),
    color: 'common.white',
    border: '2px solid',
    borderColor: 'common.white',
    display: 'flex',
    alignItems: 'center',
    justifyContent: 'center',
    cursor: 'pointer',
    zIndex: 1,
    transition: 'background-color .15s',
    '&:hover': { backgroundColor: 'error.main' },
    '& svg': { fontSize: camSize * 0.5 },
  }),
  loadingOverlay: {
    position: 'absolute',
    inset: 0,
    borderRadius: '50%',
    backgroundColor: (theme: Theme) => alpha(theme.palette.common.white, 0.78),
    display: 'flex',
    alignItems: 'center',
    justifyContent: 'center',
  },
};
